import { createSelector } from "@reduxjs/toolkit";
import isNullish from "utils/isNullish";

export const selectAppState = (state) => state?.app;

export const selectSession = ({ app }) => app?.session;

export const selectGlobalAlert = ({ app }) => app?.alerts?.global;

export const selectInlineAlert = ({ app }) => app?.alerts?.inline;

export const selectModals = ({ modals }) => modals || [];

export const selectIsModalOpen = createSelector(
  [selectModals],
  (modals) => modals.length > 0
);

export const selectIsModalTypeOpen = (modalType) => (state) =>
  selectModals(state).some((modal) => modal.modalType === modalType);

export const selectHasNoSession = createSelector([selectSession], isNullish);

export const selectApplication = createSelector(
  [selectSession],
  (session) => session?.application
);

export const selectUserInfo = createSelector(
  [selectSession],
  (session) => session?.userInfo
);

export const selectHasLoggedInAddIn = createSelector(
  [selectUserInfo],
  (userInfo) => Boolean(userInfo.addinLastLoginAt)
);

export const selectUsername = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.username || ""
);

export const selectFirstName = createSelector(
  [selectUserInfo],
  (userInfo) => userInfo?.firstName || ""
);

export const selectUserWorkspaceId = (state) =>
  state?.app?.session?.userInfo?.workspaceId;

export const selectUserRoles = ({ app }) => app?.session?.userInfo?.roles || "";

export const selectHasWorkspaces = (state) =>
  Boolean(state?.app?.session?.userInfo?.workspaces?.length);

export const selectIsLicensed = ({ app }) =>
  app?.session?.application?.licensed;

export const selectHasLicenseExpired = (state) =>
  Boolean(state?.app?.session?.application?.hasLicenseExpired);

export const selectIsLoggedIn = (state) =>
  state?.app?.session?.userInfo?.isLoggedIn;

export const selectNavBarAndSideBarDisabled = ({ app }) =>
  app?.navBarAndSideBarDisabled;

export const selectHasUnsavedChanges = (state) => state?.app?.hasUnsavedChanges;

export const selectBuildVersion = (state) =>
  state?.app?.session?.application?.buildNumber;

export const selectProductVersion = (state) =>
  state?.app?.session?.application?.productVersion;

export const selectGitCommit = (state) =>
  state?.app?.session?.application?.gitCommit;

export const selectJobQueue = createSelector(
  [selectAppState],
  (app) => app?.jobQueue || []
);

export const selectPageLoading = createSelector(
  [selectAppState],
  (app) => app?.pageLoading
);

export const selectIsGlobalLoading = createSelector(
  [selectJobQueue, selectPageLoading],
  (jobQueue, pageLoading) => jobQueue.length > 0 || pageLoading > 0
);

export const selectIsCloudMode = (state) =>
  state?.app?.session?.userInfo?.cloudMode || false;

export const selectIsSso = (state) =>
  state?.app?.session?.userInfo?.authType === "SAML";

export const selectCsrf = createSelector(
  [selectSession],
  (session) => session?.csrf
);

export const selectCsrfToken = createSelector(
  [selectCsrf],
  (csrf) => csrf?.value
);

export const selectIsGovMode = (state) =>
  state?.app?.session?.userInfo?.govMode || false;

export const selectIsInitialized = (state) =>
  Boolean(state?.app?.isInitialized);

export const selectIsSwitchingWorkspace = (state) =>
  Boolean(state?.app?.isSwitchingWorkspace);
